import { CenteredSpinner, Dock, PageScroll } from "@Components/Page";
import { gatewayUrl } from "@Constants/gatewayUrl";
import { useUserContext } from "@Hooks/contexts/useUserContext";
import { useTempDocumentPath } from "@Hooks/useTempDocumentPath";
import { styled } from "@mui/material/styles";
import { Banner, Button, EmptyState, ErrorIcon, Field, FolderIcon, GroupedList, ListRow, PlusIcon, Radio, SearchField, SearchIcon, Spinner, TextInput, focusRing, pressable } from "@UI";
import { createConfig } from "@Utils/createConfig";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
const Options = styled("div")({
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "var(--j-sp-2)"
});
const OptionCard = styled("button")({
    ...focusRing,
    ...pressable,
    borderRadius: "var(--j-r-md)",
    background: "var(--j-surface)",
    padding: "var(--j-sp-3)",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "var(--j-sp-1)",
    border: "1px solid var(--j-line-strong)",
    textAlign: "left",
    cursor: "pointer",
    color: "var(--j-ink)",
    "& > svg": {
        color: "var(--j-ink-2)",
        marginBottom: "var(--j-sp-1)"
    },
    "& > .opt-title": {
        fontWeight: 600
    },
    "& > .opt-desc": {
        fontSize: "var(--j-fs-sm)",
        lineHeight: "var(--j-lh-sm)",
        color: "var(--j-ink-2)"
    },
    "&[aria-pressed=true]": {
        border: "2px solid var(--j-accent)",
        padding: "calc(var(--j-sp-3) - 1px)"
    }
});
function upsertGroup(list, item) {
    const i = list.findIndex((g)=>g.id === item.id);
    if (i === -1) return [
        ...list,
        item
    ];
    const clone = list.slice();
    clone[i] = item;
    return clone;
}
export function StyleGroupSelector({ header, setSelectedGroupId }) {
    const { t } = useTranslation();
    const token = useUserContext((s)=>s?.user?.meta?.access_token.token);
    const [groups, setGroups] = useState([]);
    const [isLoadingGroups, setIsLoadingGroups] = useState(true);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [hasCreateError, setHasCreateError] = useState(false);
    const [isCreatingStyleGroupLoading, setIsCreatingStyleGroupLoading] = useState(false);
    const getTempDocumentPath = useTempDocumentPath();
    const [view, setView] = useState("none");
    const [newGroupName, setNewGroupName] = useState("");
    const [validationError, setValidationError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [pickedGroupId, setPickedGroupId] = useState(null);
    useEffect(()=>{
        (async ()=>{
            setIsLoadingGroups(true);
            setHasLoadError(false);
            try {
                const header = createConfig(token);
                const url = `${gatewayUrl}/plugins/word/documentStyles/styleGroup`;
                const { data } = await axios.get(url, header);
                setGroups(data);
            } catch (e) {
                console.error(e);
                setHasLoadError(true);
            } finally{
                setIsLoadingGroups(false);
            }
        })();
    }, [
        loadAttempt
    ]);
    const groupNamesLower = useMemo(()=>new Set(groups.map((g)=>g.title.trim().toLowerCase())), [
        groups
    ]);
    const filteredGroups = useMemo(()=>{
        const query = searchQuery.toLowerCase().trim();
        return groups.filter((group)=>!query || group.title.toLowerCase().includes(query)).sort((a, b)=>a.title.localeCompare(b.title));
    }, [
        groups,
        searchQuery
    ]);
    const validateGroupName = (name)=>groupNamesLower.has(name.trim().toLowerCase()) ? t("workflows.StyleConverter.groupNameExists") : null;
    async function handleCreate(name) {
        const existing = groups.find((g)=>g.title.trim().toLowerCase() === name.trim().toLowerCase());
        if (existing) {
            setSelectedGroupId(existing.id);
            return;
        }
        try {
            setHasCreateError(false);
            setIsCreatingStyleGroupLoading(true);
            const header = createConfig(token);
            const url = `${gatewayUrl}/plugins/word/documentStyles/styleGroup`;
            const path = await getTempDocumentPath();
            const body = {
                styleGroupName: name.trim(),
                path
            };
            const { data: created } = await axios.post(url, body, header);
            setGroups((prev)=>upsertGroup(prev, created));
            setSelectedGroupId(created.id);
        } catch (e) {
            console.error("Could not create the font group:", e);
            setHasCreateError(true);
        } finally{
            setIsCreatingStyleGroupLoading(false);
        }
    }
    const canCreate = !!newGroupName.trim() && !validationError;
    const isPickedVisible = filteredGroups.some((group)=>group.id === pickedGroupId);
    const focusedGroupId = isPickedVisible ? pickedGroupId : filteredGroups[0]?.id;
    const handleRadioKeyDown = (event)=>{
        const step = {
            ArrowDown: 1,
            ArrowRight: 1,
            ArrowUp: -1,
            ArrowLeft: -1
        }[event.key];
        if (!step) return;
        event.preventDefault();
        const index = filteredGroups.findIndex((group)=>group.id === focusedGroupId);
        const next = (index + step + filteredGroups.length) % filteredGroups.length;
        setPickedGroupId(filteredGroups[next].id);
        event.currentTarget.querySelectorAll("[role=radio]")[next]?.focus();
    };
    const handleCreateSubmit = ()=>{
        if (newGroupName.trim() && !isCreatingStyleGroupLoading) void handleCreate(newGroupName.trim());
    };
    const switchView = (next)=>{
        setView(next);
        setNewGroupName("");
        setValidationError(null);
        setSearchQuery("");
        setHasCreateError(false);
    };
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PageScroll, null, header, /*#__PURE__*/ React.createElement(Options, null, /*#__PURE__*/ React.createElement(OptionCard, {
        type: "button",
        "aria-pressed": view === "newGroup",
        disabled: isCreatingStyleGroupLoading,
        onClick: ()=>switchView("newGroup")
    }, /*#__PURE__*/ React.createElement(PlusIcon, null), /*#__PURE__*/ React.createElement("span", {
        className: "opt-title"
    }, t("workflows.StyleConverter.newStyleGroup")), /*#__PURE__*/ React.createElement("span", {
        className: "opt-desc"
    }, t("workflows.StyleConverter.createNewStyleGroupDescription"))), /*#__PURE__*/ React.createElement(OptionCard, {
        type: "button",
        "aria-pressed": view === "existingGroups",
        disabled: isCreatingStyleGroupLoading,
        onClick: ()=>switchView("existingGroups")
    }, /*#__PURE__*/ React.createElement(FolderIcon, null), /*#__PURE__*/ React.createElement("span", {
        className: "opt-title"
    }, t("workflows.StyleConverter.existingStyleGroups")), /*#__PURE__*/ React.createElement("span", {
        className: "opt-desc"
    }, t("workflows.StyleConverter.existingStyleGroupsDescription")))), view === "newGroup" && /*#__PURE__*/ React.createElement(Field, {
        label: t("workflows.StyleConverter.pickStyleGroupName"),
        error: !!validationError,
        helperText: validationError
    }, /*#__PURE__*/ React.createElement(TextInput, {
        autoFocus: true,
        value: newGroupName,
        disabled: isCreatingStyleGroupLoading,
        onChange: (e)=>{
            setValidationError(validateGroupName(e.target.value));
            setNewGroupName(e.target.value);
        },
        onKeyDown: (e)=>{
            if (e.key === "Enter") handleCreateSubmit();
        }
    })), view === "existingGroups" && isLoadingGroups && /*#__PURE__*/ React.createElement(CenteredSpinner, null, /*#__PURE__*/ React.createElement(Spinner, {
        size: "lg"
    })), view === "existingGroups" && !isLoadingGroups && hasLoadError && /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(ErrorIcon, null),
        tone: "neutral",
        text: t("workflows.StyleConverter.loadGroupsFailed"),
        actions: /*#__PURE__*/ React.createElement(Button, {
            variant: "tonal",
            onClick: ()=>setLoadAttempt((count)=>count + 1)
        }, t("generic.retry"))
    }), view === "existingGroups" && !isLoadingGroups && !hasLoadError && groups.length === 0 && /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(FolderIcon, null),
        title: t("workflows.StyleConverter.noExistingGroups"),
        actions: /*#__PURE__*/ React.createElement(Button, {
            variant: "tonal",
            onClick: ()=>switchView("newGroup")
        }, t("workflows.StyleConverter.createFirstGroup"))
    }), view === "existingGroups" && groups.length > 0 && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(SearchField, {
        value: searchQuery,
        onChange: setSearchQuery,
        placeholder: t("workflows.StyleConverter.searchGroups")
    }), filteredGroups.length === 0 ? /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(SearchIcon, null),
        title: t("workflows.StyleConverter.noGroupsFound")
    }) : /*#__PURE__*/ React.createElement(GroupedList, {
        role: "radiogroup",
        "aria-label": t("workflows.StyleConverter.existingStyleGroups"),
        onKeyDown: handleRadioKeyDown
    }, filteredGroups.map((group)=>{
        const isPicked = pickedGroupId === group.id;
        return /*#__PURE__*/ React.createElement(ListRow, {
            key: group.id,
            role: "radio",
            "aria-checked": isPicked,
            tabIndex: group.id === focusedGroupId ? 0 : -1,
            selected: isPicked,
            titleWeight: isPicked ? 600 : 500,
            title: group.title,
            trailing: /*#__PURE__*/ React.createElement(Radio, {
                checked: isPicked
            }),
            onClick: ()=>setPickedGroupId(group.id)
        });
    })))), view === "newGroup" && /*#__PURE__*/ React.createElement(Dock, null, hasCreateError && /*#__PURE__*/ React.createElement(Banner, {
        tone: "error"
    }, t("workflows.StyleConverter.createGroupFailed")), /*#__PURE__*/ React.createElement(Button, {
        size: "lg",
        disabled: !canCreate,
        loading: isCreatingStyleGroupLoading,
        onClick: handleCreateSubmit
    }, t(isCreatingStyleGroupLoading ? "workflows.StyleConverter.generating_font_group" : "workflows.StyleConverter.createGroup"))), view === "existingGroups" && groups.length > 0 && /*#__PURE__*/ React.createElement(Dock, null, /*#__PURE__*/ React.createElement(Button, {
        size: "lg",
        disabled: !isPickedVisible,
        onClick: ()=>isPickedVisible && pickedGroupId !== null && setSelectedGroupId(pickedGroupId)
    }, t("workflows.StyleConverter.continue"))));
}
